/**
 * Parse Pollinator's opaque `session.details` into a flat stream of render
 * blocks. The transcript may arrive in a few shapes (raw array of Claude
 * Code JSONL entries, Anthropic Messages array, a wrapping object with
 * messages/turns/transcript/history), so we normalize them all into the
 * same `RenderBlock` discriminated union before rendering.
 *
 * Tool use + tool result pairs are matched by `tool_use_id` and emitted as
 * a single `tool` block so the UI can render them as one card.
 */

export type RenderBlock =
    | { kind: 'text'; role: 'user' | 'assistant' | 'system'; text: string }
    | { kind: 'thinking'; text: string }
    | {
          kind: 'tool';
          name: string;
          input: Record<string, unknown> | unknown;
          result: unknown | null;
          isError: boolean;
      };

const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

const stringContent = (content: unknown): string => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
        return content
            .map((b) => {
                if (typeof b === 'string') return b;
                if (isObject(b) && typeof b.text === 'string') return b.text;
                return '';
            })
            .filter(Boolean)
            .join('\n');
    }
    if (content == null) return '';
    return JSON.stringify(content, null, 2);
};

const extractEntries = (details: unknown): unknown[] => {
    if (!details) return [];
    if (Array.isArray(details)) return details;
    if (isObject(details)) {
        for (const key of [
            'messages',
            'turns',
            'transcript',
            'history',
            'events'
        ]) {
            const candidate = details[key];
            if (Array.isArray(candidate)) return candidate;
        }
    }
    return [];
};

/** Resolve a Claude-Code-JSONL or plain message entry to its content blocks. */
const contentBlocksOf = (entry: unknown): unknown[] => {
    if (!isObject(entry)) return [];
    // Claude Code JSONL: entry.message.{role, content}
    if (isObject(entry.message)) {
        const c = entry.message.content;
        if (Array.isArray(c)) return c;
        if (typeof c === 'string') return [{ type: 'text', text: c }];
        return [];
    }
    // Anthropic Messages format: entry.{role, content}
    if (Array.isArray(entry.content)) return entry.content;
    if (typeof entry.content === 'string')
        return [{ type: 'text', text: entry.content }];
    // OpenAI-ish: entry.{role, content, tool_calls}
    return [];
};

const roleOf = (entry: unknown): 'user' | 'assistant' | 'system' | null => {
    if (!isObject(entry)) return null;
    const direct = typeof entry.role === 'string' ? entry.role : null;
    const nested =
        isObject(entry.message) && typeof entry.message.role === 'string'
            ? entry.message.role
            : null;
    const type = typeof entry.type === 'string' ? entry.type : null;
    const raw = (direct || nested || type || '').toLowerCase();
    if (raw.includes('user')) return 'user';
    if (raw.includes('assistant') || raw.includes('model')) return 'assistant';
    if (raw.includes('system')) return 'system';
    return null;
};

interface PendingTool {
    name: string;
    input: unknown;
}

/** Claude Code JSONL marks command-wrapper / sidechain entries that aren't
 * conversation content — skip them so the transcript reads cleanly. */
const isMetaEntry = (entry: unknown): boolean => {
    if (!isObject(entry)) return false;
    if (entry.isMeta === true) return true;
    if (entry.isSidechain === true) return true;
    const t = typeof entry.type === 'string' ? entry.type : '';
    if (
        t === 'last-prompt' ||
        t === 'permission-mode' ||
        t === 'file-history-snapshot'
    ) {
        return true;
    }
    return false;
};

export function parseTranscript(details: unknown): RenderBlock[] {
    const entries = extractEntries(details);
    const blocks: RenderBlock[] = [];
    // tool_use_id → index into `blocks` where the pending tool sits
    const pending = new Map<string, number>();

    for (const entry of entries) {
        if (isMetaEntry(entry)) continue;
        const role = roleOf(entry) ?? 'assistant';
        const content = contentBlocksOf(entry);

        // Plain string user/system entries with no nested content blocks.
        if (content.length === 0 && isObject(entry)) {
            const text = stringContent(
                (entry.content ?? entry.message ?? entry.text) as unknown
            );
            if (text) {
                blocks.push({ kind: 'text', role, text });
            }
            continue;
        }

        // Buffer plain text within an entry so consecutive text blocks coalesce.
        let textBuffer = '';
        const flushText = () => {
            if (textBuffer.trim()) {
                blocks.push({
                    kind: 'text',
                    role,
                    text: textBuffer.trimEnd()
                });
            }
            textBuffer = '';
        };

        for (const block of content) {
            if (typeof block === 'string') {
                textBuffer += (textBuffer ? '\n' : '') + block;
                continue;
            }
            if (!isObject(block)) continue;
            const btype = String(block.type ?? '');

            if (btype === 'text' && typeof block.text === 'string') {
                textBuffer += (textBuffer ? '\n' : '') + block.text;
                continue;
            }

            if (btype === 'thinking' && typeof block.thinking === 'string') {
                flushText();
                if (block.thinking.trim()) {
                    blocks.push({ kind: 'thinking', text: block.thinking });
                }
                continue;
            }

            if (btype === 'tool_use') {
                flushText();
                const id = typeof block.id === 'string' ? block.id : undefined;
                const tool: PendingTool = {
                    name: String(block.name ?? 'tool'),
                    input: block.input ?? {}
                };
                const idx = blocks.length;
                blocks.push({
                    kind: 'tool',
                    name: tool.name,
                    input: tool.input,
                    result: null,
                    isError: false
                });
                if (id) pending.set(id, idx);
                continue;
            }

            if (btype === 'tool_result') {
                flushText();
                const id =
                    typeof block.tool_use_id === 'string'
                        ? block.tool_use_id
                        : undefined;
                const output = block.content ?? null;
                const isError = block.is_error === true;
                if (id && pending.has(id)) {
                    const idx = pending.get(id)!;
                    const target = blocks[idx];
                    if (target.kind === 'tool') {
                        target.result = output;
                        target.isError = isError;
                    }
                    pending.delete(id);
                } else {
                    // Orphan result: render as a generic tool block with no input
                    blocks.push({
                        kind: 'tool',
                        name: 'tool_result',
                        input: {},
                        result: output,
                        isError
                    });
                }
                continue;
            }

            // OpenAI-style tool calls under entry.tool_calls — already handled
            // by contentBlocksOf returning [] for those, fall through.
        }
        flushText();

        // OpenAI-style: entry.message.tool_calls
        if (isObject(entry) && isObject(entry.message)) {
            const calls = entry.message.tool_calls;
            if (Array.isArray(calls)) {
                for (const c of calls) {
                    if (!isObject(c)) continue;
                    const fn = isObject(c.function) ? c.function : null;
                    blocks.push({
                        kind: 'tool',
                        name: String(fn?.name ?? c.name ?? 'tool'),
                        input: fn?.arguments ?? c.arguments ?? {},
                        result: null,
                        isError: false
                    });
                }
            }
        }
    }

    return blocks;
}
